import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/contexts/AuthContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import {
  appNavLabel,
  filterAppNavigation,
  groupAppNavigation,
  NAV_GROUP_LABEL_KEYS,
  NAV_GROUP_ORDER,
} from '@/lib/appNavigation'
import { getNavIcon } from '@/lib/navIcons'
import { cn } from '@/lib/utils'

function NavIcon({ name }) {
  const Icon = getNavIcon(name)
  return <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
}

export function HeaderSearchDialog() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { t } = useUiLocale()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)

  const filtered = useMemo(() => filterAppNavigation(user, query, t), [user, query, t])
  const grouped = useMemo(() => groupAppNavigation(filtered), [filtered])
  const flatResults = useMemo(
    () => NAV_GROUP_ORDER.flatMap((groupId) => grouped[groupId]),
    [grouped],
  )

  const openDialog = useCallback(() => {
    setOpen(true)
    setQuery('')
    setActiveIndex(0)
  }, [])

  const closeDialog = useCallback(() => {
    setOpen(false)
    setQuery('')
    setActiveIndex(0)
  }, [])

  const goTo = useCallback(
    (path) => {
      closeDialog()
      navigate(path)
    },
    [closeDialog, navigate],
  )

  useEffect(() => {
    function handleKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        openDialog()
      }

      if (event.key === 'Escape') {
        closeDialog()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [closeDialog, openDialog])

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  function handleInputKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, Math.max(flatResults.length - 1, 0)))
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    }

    if (event.key === 'Enter' && flatResults[activeIndex]) {
      event.preventDefault()
      goTo(flatResults[activeIndex].path)
    }
  }

  let resultOffset = 0

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-muted/40 text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground xl:flex 2xl:w-auto 2xl:px-3"
        aria-label={t('search.open')}
        title={t('search.placeholderShort')}
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="ml-2 hidden 2xl:inline">{t('search.placeholderShort')}</span>
        <kbd className="ml-2 hidden rounded-md border border-border/70 bg-card px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground 2xl:inline">
          Ctrl K
        </kbd>
      </button>

      <button
        type="button"
        onClick={openDialog}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground xl:hidden"
        aria-label={t('search.open')}
      >
        <Search className="h-4 w-4" />
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/40 p-4 pt-[12vh] backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t('search.title')}
        >
          <button
            type="button"
            aria-label="Close search"
            className="absolute inset-0"
            onClick={closeDialog}
          />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-border/60 bg-card text-foreground shadow-2xl">
            <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={t('search.placeholder')}
                className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="rounded-md border border-border/70 bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                Esc
              </kbd>
            </div>

            <div className="max-h-[min(60vh,420px)] overflow-y-auto p-2">
              {flatResults.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {t('search.noResults')}
                </p>
              ) : (
                NAV_GROUP_ORDER.map((groupId) => {
                  const items = grouped[groupId]
                  if (items.length === 0) {
                    return null
                  }

                  const groupStart = resultOffset
                  resultOffset += items.length

                  return (
                    <div key={groupId} className="mb-2 last:mb-0">
                      <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(NAV_GROUP_LABEL_KEYS[groupId])}
                      </p>
                      <ul>
                        {items.map((entry, index) => {
                          const flatIndex = groupStart + index
                          const isActive = flatIndex === activeIndex

                          return (
                            <li key={entry.id}>
                              <button
                                type="button"
                                className={cn(
                                  'flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-[13px] text-foreground transition-colors',
                                  isActive ? 'bg-primary/10' : 'hover:bg-muted/70',
                                )}
                                onMouseEnter={() => setActiveIndex(flatIndex)}
                                onClick={() => goTo(entry.path)}
                              >
                                <NavIcon name={entry.icon} />
                                <span className="flex-1 font-medium text-foreground">{appNavLabel(entry, t)}</span>
                                <span className="text-xs text-muted-foreground">{entry.path}</span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
